import React, { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { formatCurrency } from '../lib/utils';
import { simplePdfReports } from '../lib/reports/simplePdfReports';
import { 
  BarChart3, 
  TrendingUp, 
  Download,
  Calendar,
  PieChart,
  FileText,
  DollarSign
} from 'lucide-react';

export function Reports() {
  const { getBudgetAllocations, getTransactions, getAccounts, getCategories, state } = useApp();
  const [isGenerating, setIsGenerating] = useState(false);
  
  const budgetAllocations = getBudgetAllocations();
  const transactions = getTransactions();
  const accounts = getAccounts();
  const categories = getCategories();

  const totalSpent = budgetAllocations.reduce((sum, allocation) => sum + Math.abs(allocation.spent), 0);
  const totalAllocated = budgetAllocations.reduce((sum, allocation) => sum + allocation.allocated, 0);
  const spendingRate = totalAllocated > 0 ? (totalSpent / totalAllocated) * 100 : 0;

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateBudgetReport = () => {
    setIsGenerating(true);
    try {
      const blob = simplePdfReports.generateMonthlyBudgetVsActual(
        budgetAllocations, 
        categories, 
        state.currentMonth
      );
      downloadBlob(blob, `Budget-vs-Actual-${state.currentMonth}.pdf`);
    } catch (error) {
      console.error('Failed to generate budget report:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const generateCashFlowReport = () => {
    setIsGenerating(true);
    try {
      const blob = simplePdfReports.generateCashFlowSummary(
        transactions, 
        accounts, 
        state.currentMonth
      );
      downloadBlob(blob, `Cash-Flow-${state.currentMonth}.pdf`);
    } catch (error) {
      console.error('Failed to generate cash flow report:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const generateTransactionReport = () => {
    setIsGenerating(true);
    try {
      const blob = simplePdfReports.generateMonthlyTransactions(
        transactions, 
        accounts, 
        categories, 
        state.currentMonth
      );
      downloadBlob(blob, `Transactions-${state.currentMonth}.pdf`);
    } catch (error) {
      console.error('Failed to generate transaction report:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reports</h1>
          <p className="text-muted-foreground">Financial insights and analytics</p>
        </div>
        
        <div className="flex space-x-2">
          <Button variant="outline">
            <Calendar className="w-4 h-4 mr-2" />
            Change Period
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <TrendingUp className="w-4 h-4 mr-2" />
              Spending Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {spendingRate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              of budget used this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {transactions.length}
            </div>
            <p className="text-xs text-muted-foreground">
              this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Average Transaction</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {transactions.length > 0 
                ? formatCurrency(Math.abs(transactions.reduce((sum, t) => sum + Math.abs(t.amount), 0) / transactions.length))
                : formatCurrency(0)
              }
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Report Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Spending by Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {budgetAllocations.slice(0, 5).map((allocation, index) => (
                <div key={allocation.categoryId} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full bg-primary opacity-${100 - (index * 20)}`} />
                    <span className="text-sm">Category {allocation.categoryId.slice(-1)}</span>
                  </div>
                  <span className="text-sm font-medium">
                    {formatCurrency(Math.abs(allocation.spent))}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t">
              <p className="text-center text-sm text-muted-foreground">
                Interactive charts coming soon...
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <PieChart className="w-5 h-5 mr-2" />
              Budget vs Actual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm">Budgeted</span>
                <span className="font-medium">{formatCurrency(totalAllocated)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Spent</span>
                <span className="font-medium">{formatCurrency(totalSpent)}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-sm font-semibold">Remaining</span>
                <span className="font-bold text-emerald-600">
                  {formatCurrency(totalAllocated - totalSpent)}
                </span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t">
              <p className="text-center text-sm text-muted-foreground">
                Detailed charts and analytics coming soon...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PDF Export Reports */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Download className="w-5 h-5 mr-2" />
            Export PDF Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-6 border rounded-lg hover:bg-muted/50 transition-colors">
              <BarChart3 className="w-8 h-8 text-primary mx-auto mb-3" />
              <h4 className="font-medium mb-2">Monthly Budget vs Actual</h4>
              <p className="text-xs text-muted-foreground mb-4">
                Category table with over/under budget flags
              </p>
              <Button 
                onClick={generateBudgetReport} 
                disabled={isGenerating}
                className="w-full"
              >
                <FileText className="w-4 h-4 mr-2" />
                {isGenerating ? 'Generating...' : 'Export Budget Report'}
              </Button>
            </div>
            
            <div className="text-center p-6 border rounded-lg hover:bg-muted/50 transition-colors">
              <DollarSign className="w-8 h-8 text-primary mx-auto mb-3" />
              <h4 className="font-medium mb-2">Cash Flow Summary</h4>
              <p className="text-xs text-muted-foreground mb-4">
                Opening balance, inflows, outflows, closing balance
              </p>
              <Button 
                onClick={generateCashFlowReport} 
                disabled={isGenerating}
                className="w-full"
              >
                <FileText className="w-4 h-4 mr-2" />
                {isGenerating ? 'Generating...' : 'Export Cash Flow'}
              </Button>
            </div>
            
            <div className="text-center p-6 border rounded-lg hover:bg-muted/50 transition-colors">
              <Calendar className="w-8 h-8 text-primary mx-auto mb-3" />
              <h4 className="font-medium mb-2">Monthly Transactions</h4>
              <p className="text-xs text-muted-foreground mb-4">
                Complete transaction list with date, payee, memo, amount
              </p>
              <Button 
                onClick={generateTransactionReport} 
                disabled={isGenerating}
                className="w-full"
              >
                <FileText className="w-4 h-4 mr-2" />
                {isGenerating ? 'Generating...' : 'Export Transactions'}
              </Button>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-muted/30 rounded-lg">
            <p className="text-sm text-center text-muted-foreground">
              <strong>Current Period:</strong> {state.currentMonth} • 
              All reports are generated locally and contain encrypted data only
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}